import { useState } from 'react';
import { Copy } from 'iconoir-react';
import Download from '@listening/assets/icons/download.svg?react';
import Share2 from '@listening/assets/icons/share2.svg?react';
import { toast } from 'sonner';
import { cn } from '@listening/utils/react-helpers';
import {
  dialogSurface,
  primaryButtonSurface,
  secondaryButtonSurface,
  wellSurface,
} from '@/components/web-fe/design-v2/surfaces';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@listening/frontend/components/radix/dialog';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@listening/frontend/components/radix/toggle-group';
import {
  formatSummaryCsv,
  formatSummaryText,
  summaryExportFilename,
  type SummaryExport,
} from '@listening/frontend/document-insights/export';
import * as m from '@/paraglide/messages';
import { useCopyText } from '@/lib/clipboard';
import { useAtom } from '@effect/atom-react';
import { Cause, Data, Effect, Exit, Option } from 'effect';
import { Atom } from 'effect/reactivity';

class SummaryShareError extends Data.TaggedError('SummaryShareError')<{
  /** The person closed the share sheet. */
  readonly aborted: boolean;
}> {}

const shareSummaryAtom = Atom.fn((data: ShareData) =>
  Effect.tryPromise({
    try: () => navigator.share(data),
    catch: (error) =>
      new SummaryShareError({
        aborted: error instanceof DOMException && error.name === 'AbortError',
      }),
  }),
);

export function SummaryExportButton(content: SummaryExport) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type='button'
          aria-label={m.summary_export_title()}
          className='inline-flex h-8 items-center gap-1.5 rounded-[var(--v2-button-radius)] px-2.5 font-(family-name:--v2-font) text-[12px] font-medium text-[var(--v2-text-muted)] transition-colors hover:bg-[var(--v2-chip-bg)] hover:text-[var(--v2-text-primary)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--v2-accent)]'
        >
          <Download className='size-3.5' />
          {m.dialog_export()}
        </button>
      </DialogTrigger>
      <SummaryExportDialog {...content} />
    </Dialog>
  );
}

function SummaryExportDialog(content: SummaryExport) {
  const [format, setFormat] = useState<'TXT' | 'CSV'>('TXT');
  const text = formatSummaryText(content, {
    summary: m.common_summary(),
    keyPoints: m.public_docs_key_points(),
  });
  const body =
    format === 'TXT'
      ? text
      : formatSummaryCsv(content, {
          title: m.dialog_note_title(),
          summary: m.common_summary(),
          keyPoints: m.public_docs_key_points(),
        });
  const { copy, copying } = useCopyText();
  const [shareResult, share] = useAtom(shareSummaryAtom, {
    mode: 'promiseExit',
  });
  const download = () => {
    try {
      const blob = new Blob(format === 'CSV' ? ['\uFEFF', body] : [body], {
        type:
          format === 'CSV'
            ? 'text/csv;charset=utf-8'
            : 'text/plain;charset=utf-8',
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${summaryExportFilename(content.title)}.${format.toLowerCase()}`;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error(m.summary_export_failed());
    }
  };

  return (
    <DialogContent
      includeCloseButton
      variant='app'
      aria-describedby={undefined}
      className={cn(
        'flex max-h-[calc(100%-2rem)] w-[calc(100%-2rem)] max-w-[28rem] flex-col overflow-y-auto',
        dialogSurface,
      )}
    >
      <DialogTitle>{m.summary_export_title()}</DialogTitle>
      <div className='flex shrink-0 items-center justify-between gap-4'>
        <span className='text-[13px] font-medium text-[var(--v2-text-muted)]'>
          {m.dialog_format()}
        </span>
        <ToggleGroup
          type='single'
          variant='app'
          value={format}
          aria-label={m.dialog_format()}
          onValueChange={(value) => {
            if (value === 'TXT' || value === 'CSV') setFormat(value);
          }}
        >
          <ToggleGroupItem variant='app' value='TXT'>
            TXT
          </ToggleGroupItem>
          <ToggleGroupItem variant='app' value='CSV'>
            CSV
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
      <div
        className={cn(
          'min-h-0 overflow-y-auto p-[var(--v2-gap-4)]',
          wellSurface,
        )}
      >
        {format === 'CSV' ? (
          <pre className='font-mono text-[12px] leading-relaxed break-words whitespace-pre-wrap text-[var(--v2-text-primary)]'>
            {body}
          </pre>
        ) : (
          <div className='space-y-[var(--v2-gap-4)]'>
            <p className='text-[15px] leading-snug font-semibold break-words text-[var(--v2-text-primary)]'>
              {content.title}
            </p>
            <section className='space-y-2'>
              <h3 className='text-[11px] font-semibold tracking-wide text-[var(--v2-text-muted)] uppercase'>
                {m.common_summary()}
              </h3>
              <p className='text-[14px] leading-relaxed break-words whitespace-pre-wrap text-[var(--v2-text-primary)]'>
                {content.summary}
              </p>
            </section>
            {content.keyPoints.length > 0 && (
              <section className='space-y-2'>
                <h3 className='text-[11px] font-semibold tracking-wide text-[var(--v2-text-muted)] uppercase'>
                  {m.public_docs_key_points()}
                </h3>
                <ul className='list-disc space-y-2 pl-4 text-[14px] leading-relaxed break-words text-[var(--v2-text-primary)] marker:text-[var(--v2-text-muted)]'>
                  {content.keyPoints.map((point, index) => (
                    <li key={index}>{point}</li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </div>
      <div className='flex shrink-0 flex-wrap gap-[var(--v2-gap-2)]'>
        <button
          type='button'
          className={cn(
            'flex h-10 min-w-24 flex-1 items-center justify-center gap-2 text-[13px] font-semibold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--v2-accent)]',
            secondaryButtonSurface,
          )}
          disabled={copying}
          onClick={() => {
            void copy(body).then((exit) => {
              if (Exit.isSuccess(exit)) toast.success(m.dialog_copied());
              else toast.error(m.summary_export_copy_failed());
            });
          }}
        >
          <Copy className='size-4 shrink-0' />
          {m.common_copy()}
        </button>
        {typeof navigator !== 'undefined' && 'share' in navigator && (
          <button
            type='button'
            className={cn(
              'flex h-10 min-w-24 flex-1 items-center justify-center gap-2 text-[13px] font-semibold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--v2-accent)]',
              secondaryButtonSurface,
            )}
            disabled={shareResult.waiting}
            onClick={() => {
              void share({ title: content.title, text: body }).then((exit) => {
                if (
                  Exit.isFailure(exit) &&
                  !Option.exists(
                    Cause.findErrorOption(exit.cause),
                    (error) => error.aborted,
                  )
                )
                  toast.error(m.summary_share_failed());
              });
            }}
          >
            <Share2 className='size-4 shrink-0' />
            {m.common_share()}
          </button>
        )}
        <button
          type='button'
          className={cn(
            'flex h-10 min-w-24 flex-1 items-center justify-center gap-2 text-[13px] font-semibold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--v2-accent)]',
            primaryButtonSurface,
          )}
          onClick={download}
        >
          <Download className='size-4 shrink-0' />
          {m.common_download()}
        </button>
      </div>
    </DialogContent>
  );
}
